import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName } from "@/lib/utils";

export interface ConversationWithMeta {
  id: string;
  client_id: string;
  artisan_id: string;
  annonce_id: string | null;
  status: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  client_unread_count: number;
  artisan_unread_count: number;
  updated_at: string;
  /** The ID of the other participant (not the current user). */
  other_id: string;
  /** Display name of the other participant. */
  other_name: string;
  /** Title of the linked annonce, if any. */
  annonce_title: string | null;
  /** Unread count for the current user. */
  unread_count: number;
}

/**
 * Loads all conversations for the current user.
 *
 * Performance: previously this used N+1 queries (1 conversation list
 * + 2 per conversation for profiles and annonces). This hook uses exactly
 * 2 queries regardless of conversation count:
 *  1. conversations joined with annonces(title)
 *  2. batch profiles fetch for all other participants
 */
export function useConversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationWithMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // Query 1: conversations + annonce title in a single joined select
    const { data, error } = await supabase
      .from("conversations")
      .select("*, annonces(title)")
      .or(`client_id.eq.${user.id},artisan_id.eq.${user.id}`)
      .order("last_message_at", { ascending: false, nullsFirst: false });

    if (error || !data) return;

    // Determine the other participant for each conversation
    const otherIds = [
      ...new Set(
        data.map((c: any) =>
          c.client_id === user.id ? c.artisan_id : c.client_id
        )
      ),
    ];

    // Query 2: batch-fetch all other participant profiles at once
    const { data: profiles } = otherIds.length
      ? await supabase
          .from("profiles")
          .select("user_id, display_name, first_name, last_name")
          .in("user_id", otherIds)
      : { data: [] };

    const profileMap = new Map(
      (profiles || []).map((p: any) => [p.user_id, p])
    );

    // Query 3: fallback pour les artisans sans nom dans profiles
    // On récupère raison_sociale depuis artisan_profiles pour les IDs sans nom résolu.
    const unnamedIds = otherIds.filter((id) => {
      const p = profileMap.get(id);
      return !p?.display_name && !p?.first_name && !p?.last_name;
    });
    const { data: artisanProfiles } = unnamedIds.length
      ? await supabase
          .from("artisan_profiles")
          .select("user_id, raison_sociale")
          .in("user_id", unnamedIds)
      : { data: [] };

    const artisanProfileMap = new Map(
      (artisanProfiles || []).map((a: any) => [a.user_id, a])
    );

    const result: ConversationWithMeta[] = data.map((c: any) => {
      const otherId =
        c.client_id === user.id ? c.artisan_id : c.client_id;
      const profile = profileMap.get(otherId);
      const myUnread =
        c.client_id === user.id
          ? c.client_unread_count
          : c.artisan_unread_count;

      // Résolution du nom : profile → raison_sociale artisan → "Artisan"
      const resolvedName =
        getDisplayName(profile, "") ||
        artisanProfileMap.get(otherId)?.raison_sociale ||
        "Artisan";

      return {
        id: c.id,
        client_id: c.client_id,
        artisan_id: c.artisan_id,
        annonce_id: c.annonce_id,
        status: c.status,
        last_message_at: c.last_message_at,
        last_message_preview: c.last_message_preview,
        client_unread_count: c.client_unread_count,
        artisan_unread_count: c.artisan_unread_count,
        updated_at: c.updated_at,
        other_id: otherId,
        other_name: resolvedName,
        annonce_title: c.annonces?.title ?? null,
        unread_count: myUnread,
      };
    });

    setConversations(result);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
    if (!user) return;

    // Subscribe to real-time conversation updates.
    // Two explicit filters (client_id + artisan_id) sont nécessaires pour
    // garantir la réception des INSERT d'une autre session sous RLS —
    // sans filtre, Supabase Realtime peut ne pas propager les nouvelles lignes.
    // La table conversations doit avoir REPLICA IDENTITY FULL pour que les
    // événements UPDATE filtrés par artisan_id fonctionnent correctement.
    const channel = supabase
      .channel(`conversations-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations", filter: `client_id=eq.${user.id}` },
        () => load()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations", filter: `artisan_id=eq.${user.id}` },
        () => load()
      )
      .subscribe();

    // Polling fallback toutes les 30 secondes — filet de sécurité si
    // Supabase Realtime rate un INSERT cross-session (limitation connue
    // avec RLS selon la version du serveur Realtime).
    const pollInterval = setInterval(load, 30_000);

    // Rechargement immédiat quand l'onglet redevient actif (l'utilisateur
    // revient sur la page après avoir consulté une autre).
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [load, user]);

  return { conversations, loading, reload: load };
}
